import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import AppShell from './AppShell';
import { API_BASE_URL } from '../config/api';
import { saveAuth } from '../utils/auth';
import { Input } from './Input';
import { ChevronLeft } from 'lucide-react';
import './Login.css';
import './LoginMethod.css';

const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ usernameOrEmail, password }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        // 仅在开发环境输出调试信息
        if (process.env.NODE_ENV === 'development') {
          console.log('Login - 服务器返回的用户信息:', data.data);
          console.log('Login - 用户角色:', data.data.user?.role);
        }
        saveAuth(data.data);
        // 验证保存后的数据（仅开发环境）
        if (process.env.NODE_ENV === 'development') {
          const savedUser = JSON.parse(localStorage.getItem('user_info') || '{}');
          console.log('Login - 保存后的用户信息:', savedUser);
          console.log('Login - 保存后的用户角色:', savedUser.role);
        }
        navigate('/');
      } else {
        setError(data.error || '登录失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell centerBody>
      <div className="login-box">
        <div className="login-flow-back-row">
          <button type="button" className="login-method-back login-method-back--top" onClick={() => navigate('/sign-in', { state: location.state })}>
            <ChevronLeft size={18} strokeWidth={2.25} aria-hidden />
            <span>返回</span>
          </button>
        </div>
        <div className="login-title-brand">luludomi</div>
        <h2>登录</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="usernameOrEmail">用户名或邮箱</label>
            <Input
              type="text"
              id="usernameOrEmail"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">密码</label>
            <div className="password-input-container">
              <Input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? '登录中...' : '登录'}
          </button>
        </form>
        <div className="auth-links">
          <Link to="/sign-in" state={location.state}>其他登录方式</Link>
          <span style={{ margin: '0 10px', color: '#999' }}>|</span>
          <span>还没有账号？</span>
          <Link to="/register" state={location.state}>立即注册</Link>
        </div>
      </div>
    </AppShell>
  );
};

export default Login;

